import { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import type { ProcessedRecommendationItem } from '@/domains/productDiscovery/Recommendation/interfaces/msRecommendation';
import type { Recommendations } from '@/domains/productDiscovery/Recommendation/interfaces/productRecommendation';

import { PRODUCT_BUNDLE_ENGINE_NAMES } from '../../ProductBundle.interfaces';
import type {
  UseRecommendationsFetchState,
  UseRecommendationsReturn,
} from './useRecommendations.interfaces';

export function useProductBundleRecommendations(): UseRecommendationsReturn {
  const recommendationsStateSelector = useCallback(
    (
      state: Partial<SpartacuxProductDiscoveryDomainState>,
    ): UseRecommendationsFetchState => ({
      isFetching:
        state.productDiscovery?.productRecommendation?.fetching ||
        (!state.productDiscovery?.productRecommendation?.fetching &&
          !state.productDiscovery?.productRecommendation?.recommendations?.filter(
            (reco) => reco.engineName === PRODUCT_BUNDLE_ENGINE_NAMES.BUNDLE,
          ).length),

      error: state.productDiscovery?.productRecommendation?.error,
    }),
    [],
  );

  const recommendationsSelector = useCallback(
    (state: Partial<SpartacuxProductDiscoveryDomainState>): Recommendations =>
      state.productDiscovery?.productRecommendation?.recommendations?.filter(
        (engine) => PRODUCT_BUNDLE_ENGINE_NAMES.BUNDLE === engine.engineName,
      ) ?? [],
    [],
  );

  const recommendationsConfigSelector = useCallback(
    (
      state: Partial<SpartacuxProductDiscoveryDomainState>,
    ): ProcessedRecommendationItem[] =>
      state.productDiscovery?.productRecommendation?.recommendationsConfig?.filter(
        (engine) => PRODUCT_BUNDLE_ENGINE_NAMES.BUNDLE === engine.engineName,
      ) ?? [],
    [],
  );

  const state = useSelector(recommendationsStateSelector, shallowEqual);

  const recommendations = useSelector(recommendationsSelector, shallowEqual);

  const configs = useSelector(recommendationsConfigSelector, shallowEqual);

  const hasProducts = useMemo(
    () =>
      configs.some(
        (config) =>
          (config.modelIds?.length ?? 0) > 0 ||
          (config.categoryIds?.length ?? 0) > 0 ||
          (config.productIds?.length ?? 0) > 0,
      ),
    [configs],
  );

  const skeletons = useMemo(
    () =>
      (configs.find((config) => config.modelIds)?.modelIds?.slice(0, 2)
        .length ?? 0) + 1,
    [configs],
  );

  return [state, recommendations, hasProducts, skeletons];
}
